/* eslint-disable */
import {
    Box,
    Checkbox,
    CircularProgress,
    Skeleton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    InputAdornment,
    Paper,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Toolbar,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import { DeleteForeverOutlined, Search } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useDispatch, useSelector } from 'react-redux';
import { Controls } from '../../components/controls/Controls';
import PageHeader from '../../components/PageHeader';
import Popup from '../../components/Popup';
import useTable from '../../components/useTable';
import { loadMovimientosBancarios } from '../../redux/ducks/movimientosBancarios';
import { setSnackbar } from '../../redux/ducks/snackbar';
import { loadBancos } from '../../redux/ducks/bancos';
import { loadCuentasBancarias } from '../../redux/ducks/cuentasBancarias';
import MovimientosBancariosForm from './MovimientosBancariosForm';
import { buscarControlIngreso, toFechaISO } from './controlIngresoSync';
import ConfirmDialog from '../../components/ConfirmDialog';
import http from '../../http-common';
import { filterOnAnyField } from '../../utils';

const useStyles = makeStyles((theme) => ({
    pageContent: {
        margin: theme.spacing(2),
        marginTop: theme.spacing(1),
        padding: theme.spacing(2),
    },
    filterSection: {
        margin: theme.spacing(2),
        marginBottom: 0,
        padding: theme.spacing(2),
    },
    filterLabel: {
        marginBottom: theme.spacing(2),
        fontWeight: 'bold',
        color: '#333996',
    },
    selectControl: {
        minWidth: 280,
        marginRight: theme.spacing(3),
    },
    newButton: {},
    refreshButton: {},
    tableCell: {
        border: '1px solid #c0c0c0',
        padding: '4px 8px',
        fontSize: '0.82rem',
    },
    tableRow: {
        '& td': {
            border: '1px solid #c0c0c0',
            padding: '4px 8px',
            fontSize: '0.82rem',
        },
    },
    tableHead: {
        '& th': {
            border: '1px solid #9e9e9e',
            padding: '6px 8px',
            fontSize: '0.82rem',
            backgroundColor: '#e8eaf6',
        },
    },
}));

const MESES_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const monedaFormatter = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
});

const formatMoneda = (value) => {
    if (value == null) return '$ 0.00';
    return monedaFormatter.format(value);
};

const formatFecha = (fecha) => {
    if (!fecha) return '';
    // Handle both "2026-08-13" and "2026-08-13T00:00:00" formats
    const date = new Date(fecha.includes('T') ? fecha : `${fecha}T00:00:00`);
    if (Number.isNaN(date.getTime())) return fecha;
    const day = date.getDate();
    const mes = MESES_ES[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${mes}-${year}`;
};

const FILTRO_SX = {
    width: 160,
    '& .MuiOutlinedInput-root': { height: 36.5 },
    '& .MuiInputLabel-root': { top: -8 },
    '& .MuiInputLabel-shrink': { top: 0 },
};

// El texto vive aquí para que escribir no re-renderice toda la pantalla (y su tabla);
// la pantalla solo se entera al presionar Enter.
function FiltroNumerico({ label, onApply }) {
    const [texto, setTexto] = useState('');
    return (
        <Controls.Input
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            label={label}
            onChange={(e) => {
                const val = e.target.value;
                if (val === '' || /^\d+$/.test(val)) setTexto(val);
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onApply(texto);
            }}
            sx={FILTRO_SX}
            value={texto}
        />
    );
}

const headCells = [
    { id: 'aprobar', label: 'Aprobar', disableSorting: true },
    { id: 'id', label: 'ID' },
    { id: 'fecha', label: 'Fecha' },
    { id: 'referencia', label: 'Cliente' },
    { id: 'tipo', label: 'Clasificación' },
    { id: 'factura', label: 'Factura' },
    { id: 'folio', label: 'Folio' },
    { id: 'descripcion', label: 'Descripción' },
    { id: 'deposito', label: 'Depósito' },
    { id: 'retiro', label: 'Retiro' },
    { id: 'saldoActual', label: 'Saldo Movimiento' },
    { id: 'actions', label: 'Acciones', disableSorting: true },
];

export default function MovimientosBancarios() {
    const [openPopup, setOpenPopup] = useState(false);
    const [unsavedGastosDialogOpen, setUnsavedGastosDialogOpen] = useState(false);
    // El formulario registra aquí una función que el padre llama al intentar cerrar.
    // La función devuelve { hasUnsaved, cleanup } con closure fresco sobre el estado del form.
    const checkUnsavedRef = useRef(null);
    const pendingCleanupRef = useRef(null);
    const classes = useStyles();
    const [filterFn, setFilterFn] = useState({ fn: (items) => items });
    const [recordForEdit, setRecordForEdit] = useState();
    const [selectedBanco, setSelectedBanco] = useState('');
    const [selectedCuenta, setSelectedCuenta] = useState('');
    const [saldoCuenta, setSaldoCuenta] = useState(null);
    const [nuevoSaldoActual, setNuevoSaldoActual] = useState(null);
    const [saldoError, setSaldoError] = useState(false);
    const [saldoLoading, setSaldoLoading] = useState(false);
    const [movimientosLoading, setMovimientosLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [approvePassword, setApprovePassword] = useState('');
    const [deleteErrorDialogOpen, setDeleteErrorDialogOpen] = useState(false);
    // Cambiar la key de un FiltroNumerico lo reinicia (vacío) cuando se aplica otro filtro
    const [resetFiltroId, setResetFiltroId] = useState(0);
    const [resetFiltroFolio, setResetFiltroFolio] = useState(0);
    const [filterSearch, setFilterSearch] = useState('');
    const [filterFechaInicio, setFilterFechaInicio] = useState('');
    const [filterFechaFin, setFilterFechaFin] = useState('');
    // Valores "congelados" al presionar el botón — el useMemo solo depende de estos
    const [appliedFechaInicio, setAppliedFechaInicio] = useState('');
    const [appliedFechaFin, setAppliedFechaFin] = useState('');
    const [dateFilterApplied, setDateFilterApplied] = useState(false);
    const [soloPendientes, setSoloPendientes] = useState(false);

    const dispatch = useDispatch();
    const history = useHistory();

    const bancos = useSelector((state) => state.bancos.bancos);
    const cuentasBancarias = useSelector((state) => state.cuentasBancarias.cuentasBancarias);
    const movimientosBancarios = useSelector((state) => state.movimientosBancarios.movimientosBancarios);

    // Dropdowns options
    const bancosOptions = bancos.map((b) => ({ id: b.id, title: b.nombre }));

    // Filter cuentas by selected banco
    const cuentasFiltradas = cuentasBancarias.filter((c) => c.idBanco === selectedBanco);
    const cuentasOptions = cuentasFiltradas.map((c) => ({
        id: c.id,
        title: c.numeroCuenta ? `${c.numeroCuenta} - ${c.titular || ''}` : c.titular,
    }));

    // Aplica filtro de fechas solo con los valores "aplicados" (capturados al presionar el botón)
    // No depende de filterFechaInicio/filterFechaFin en vivo → no filtra mientras el usuario escribe
    const movimientosFiltradosPorFecha = useMemo(() => {
        const data = movimientosBancarios || [];
        if (!dateFilterApplied || !appliedFechaInicio) return data;
        return data.filter((item) => {
            if (!item.fecha) return false;
            // Extrae YYYY-MM-DD por regex — funciona con cualquier formato del API
            const match = String(item.fecha).match(/(\d{4}-\d{2}-\d{2})/);
            if (!match) return false;
            const fechaStr = match[1];
            if (appliedFechaFin) return fechaStr >= appliedFechaInicio && fechaStr <= appliedFechaFin;
            return fechaStr === appliedFechaInicio;
        });
    }, [movimientosBancarios, dateFilterApplied, appliedFechaInicio, appliedFechaFin]);

    const {
        TblContainer,
        TblHead,
        TblPagination,
        recordsAfterPagingAndSorting,
    } = useTable(movimientosFiltradosPorFecha, headCells, filterFn, 100, 'id', 'desc');

    const limpiarBusquedaYFechas = () => {
        setSoloPendientes(false);
        setFilterSearch('');
        setFilterFechaInicio('');
        setFilterFechaFin('');
        setAppliedFechaInicio('');
        setAppliedFechaFin('');
        setDateFilterApplied(false);
    };

    const cargarMovimientos = (idBanco, idCuenta) => {
        setMovimientosLoading(true);
        dispatch(loadMovimientosBancarios(idBanco, idCuenta));
    };

    useEffect(() => {
        setMovimientosLoading(false);
    }, [movimientosBancarios]);

    const cargarSaldoCalculado = async (idCuenta) => {
        setSaldoLoading(true);
        setSaldoError(false);
        setSaldoCuenta(null);
        setNuevoSaldoActual(null);
        try {
            const resp = await http.get(
                `/movimientos-bancarios/calcula-saldo-actual/${idCuenta}`,
                { validateStatus: () => true },
            );
            if (resp.status === 200 && resp.data?.saldoCalculado != null) {
                setSaldoCuenta(resp.data.saldoCalculado);
                setNuevoSaldoActual(resp.data.saldoCalculado);
                setSaldoError(false);
            } else {
                setSaldoCuenta(null);
                setNuevoSaldoActual(null);
                setSaldoError(true);
            }
        } catch {
            setSaldoCuenta(null);
            setNuevoSaldoActual(null);
            setSaldoError(true);
        } finally {
            setSaldoLoading(false);
        }
    };

    // Load bancos and cuentas on mount; movimientos only when both filters are selected
    useEffect(() => {
        dispatch(loadBancos());
        dispatch(loadCuentasBancarias());

        const applyDefault = async () => {
            try {
                const resp = await http.get('/cuentas-bancarias/BuscaDefault', { validateStatus: () => true });
                if (resp.status === 200 && resp.data?.idBanco && resp.data?.idCuenta) {
                    const { idBanco, idCuenta } = resp.data;
                    setSelectedBanco(idBanco);
                    setSelectedCuenta(idCuenta);
                    cargarMovimientos(idBanco, idCuenta);
                    cargarSaldoCalculado(idCuenta);
                }
            } catch {
                // 404 u otro error — no hacer nada, el usuario selecciona manualmente
            }
        };

        applyDefault();
    }, [dispatch]);

    // Reset cuenta and reload movimientos when banco changes
    const handleBancoChange = (e) => {
        setSelectedBanco(e.target.value);
        setSelectedCuenta('');
        setSaldoCuenta(null);
        setNuevoSaldoActual(null);
        setSaldoError(false);
    };

    // Load movimientos from API when cuenta is selected
    const handleCuentaChange = (e) => {
        const idCuenta = e.target.value;
        setSelectedCuenta(idCuenta);
        if (selectedBanco && idCuenta) {
            cargarMovimientos(selectedBanco, idCuenta);
            cargarSaldoCalculado(idCuenta);
        }
    };

    const addOrEdit = async (movimiento, resetForm, shouldCreateControlIngreso = false, onMovimientoSaved = null) => {
        const isNew = !movimiento.id || movimiento.id === 0;
        const payload = {
            ...movimiento,
            idBanco: selectedBanco,
            idCuenta: selectedCuenta,
        };
        // Usamos HTTP directo para obtener el ID del registro guardado
        let savedId = movimiento.id;
        try {
            const resp = await http.post('/movimientos-bancarios', payload);
            // El backend puede retornar el objeto completo ({id: X, ...}) o solo el ID como número
            const respData = resp?.data;
            if (respData && typeof respData === 'object' && respData.id) {
                savedId = respData.id;
            } else if (typeof respData === 'number' && respData > 0) {
                savedId = respData;
            }
            // Si no se pudo extraer el ID de la respuesta, savedId queda con movimiento.id
            dispatch(setSnackbar(true, 'success', isNew ? 'El registro se agregó correctamente' : 'El registro se actualizó correctamente'));
        } catch {
            dispatch(setSnackbar(true, 'error', isNew ? 'Error al agregar el registro' : 'Error al actualizar el registro'));
            return;
        }

        // Para registros nuevos: actualizar los gastos del viaje con el ID del movimiento recién creado
        if (onMovimientoSaved && savedId) {
            try {
                await onMovimientoSaved(savedId);
            } catch {
                // Error al actualizar idMovimientoBancario en gastos — el movimiento ya se guardó
            }
        }

        // Crear registro en control ingresos si aplica
        if (shouldCreateControlIngreso && savedId) {
            try {
                await http.post('/control-ingresos', {
                    cliente: movimiento.referencia || null,
                    factura: movimiento.factura || null,
                    fecha: toFechaISO(movimiento.fecha),
                    idCuentaBancaria: selectedCuenta || null,
                    idMovimientosBancarios: savedId,
                    monto: parseFloat(movimiento.deposito) || 0,
                });
            } catch {
                dispatch(setSnackbar(true, 'error', 'El movimiento se guardó pero no se pudo crear el Control Ingreso'));
            }
        }

        // Si es edición y cambió deposito o retiro, ajustar movimientos posteriores
        if (!isNew && savedId) {
            const depositoCambio = parseFloat(movimiento.deposito || 0) !== parseFloat(recordForEdit?.deposito || 0);
            const retiroCambio = parseFloat(movimiento.retiro || 0) !== parseFloat(recordForEdit?.retiro || 0);
            if (depositoCambio || retiroCambio) {
                try {
                    await http.get(
                        `/movimientos-bancarios/ajustarMovimientos/${savedId}/${selectedCuenta}`,
                        { validateStatus: () => true },
                    );
                } catch {
                    // si falla el ajuste, continuamos igual
                }
            }
            // Sincronizar monto, cliente, factura y fecha con control ingresos (si no se acaba de crear)
            const referenciaCambio = (movimiento.referencia || '') !== (recordForEdit?.referencia || '');
            const facturaCambio = (movimiento.factura || '') !== (recordForEdit?.factura || '');
            const fechaCambio = toFechaISO(movimiento.fecha) !== toFechaISO(recordForEdit?.fecha);
            const hayCambios = depositoCambio || referenciaCambio || facturaCambio || fechaCambio;
            if (movimiento.reflejadoIngresos && !shouldCreateControlIngreso && hayCambios) {
                let actualizado = false;
                try {
                    const ingreso = await buscarControlIngreso(savedId, selectedCuenta);
                    if (ingreso) {
                        const saveResp = await http.post('/control-ingresos', {
                            ...ingreso,
                            cliente: movimiento.referencia || null,
                            factura: movimiento.factura || null,
                            fecha: toFechaISO(movimiento.fecha),
                            idCuentaBancaria: ingreso.idCuentaBancaria ?? selectedCuenta,
                            monto: parseFloat(movimiento.deposito) || 0,
                        }, { validateStatus: () => true });
                        actualizado = saveResp.status >= 200 && saveResp.status < 300;
                    }
                } catch {
                    actualizado = false;
                }
                if (!actualizado) {
                    dispatch(setSnackbar(true, 'warning', 'El movimiento se guardó pero no se pudo actualizar Control Ingresos'));
                }
            }
        }

        setOpenPopup(false);
        resetForm();
        setTimeout(async () => {
            cargarMovimientos(selectedBanco, selectedCuenta);
            cargarSaldoCalculado(selectedCuenta);
        }, 500);
    };

    // Cierre controlado del popup: pregunta al formulario si hay gastos sin guardar.
    // El formulario registra en checkUnsavedRef una función con closure fresco sobre su estado.
    const handleClosePopup = (val) => {
        if (!val && checkUnsavedRef.current) {
            const result = checkUnsavedRef.current();
            if (result.hasUnsaved) {
                pendingCleanupRef.current = result.cleanup;
                setUnsavedGastosDialogOpen(true);
                return;
            }
        }
        setOpenPopup(val);
    };

    // "Cerrar sin guardar": ejecuta la función de limpieza que el formulario preparó
    const handleCloseWithoutSaving = async () => {
        if (pendingCleanupRef.current) {
            await pendingCleanupRef.current();
            pendingCleanupRef.current = null;
        }
        setUnsavedGastosDialogOpen(false);
        setOpenPopup(false);
    };

    const openInPopup = (item) => {
        setRecordForEdit(item);
        setOpenPopup(true);
    };

    const [confirmDialog, setConfirmDialog] = useState({
        title: 'title',
        subtitle: 'subtitle',
        isOpen: false,
    });

    const [reflejadoDialog, setReflejadoDialog] = useState({ isOpen: false, item: null });
    const [folioDialog, setFolioDialog] = useState({ isOpen: false, item: null });

    const ejecutarBorrado = async (item) => {
        // Paso 1: Si el movimiento tenía folio, limpiar primero los gastos asociados
        if (item.folio) {
            try {
                const viajeResp = await http.get(`/viajes/${item.folio}`);
                const viaje = viajeResp?.data;
                if (viaje && Array.isArray(viaje.otrosGastos)) {
                    const gastosRestantes = viaje.otrosGastos.filter(
                        (g) => !(g.idMovimientoBancario === item.id && g.idCuentaBancaria === item.idCuenta),
                    );
                    if (gastosRestantes.length !== viaje.otrosGastos.length) {
                        await http.post('/viajes?password=', {
                            ...viaje,
                            otrosGastos: gastosRestantes,
                        }, {
                            headers: { 'Content-Type': 'application/json' },
                            validateStatus: () => true,
                        });
                    }
                }
            } catch {
                // Si falla la limpieza de gastos, continuamos igual e intentamos borrar
            }
        }

        // Paso 2: Borrar el movimiento bancario
        const resp = await http.delete(`/movimientos-bancarios/${item.id}/${item.idCuenta}`, {
            validateStatus: () => true,
        });
        if (!resp || resp.status < 200 || resp.status >= 300) {
            setDeleteErrorDialogOpen(true);
            return;
        }

        // Ajustar movimientos a partir del registro anterior al borrado
        const registroAnterior = (movimientosBancarios || [])
            .filter((m) => m.id < item.id && m.idCuenta === item.idCuenta)
            .sort((a, b) => b.id - a.id)[0];
        if (registroAnterior) {
            try {
                await http.get(
                    `/movimientos-bancarios/ajustarMovimientos/${registroAnterior.id}/${item.idCuenta}`,
                    { validateStatus: () => true },
                );
            } catch {
                // si falla el ajuste, continuamos igual
            }
        }

        dispatch(setSnackbar(true, 'info', 'Registro borrado exitosamente'));
        cargarMovimientos(selectedBanco, selectedCuenta);
        cargarSaldoCalculado(selectedCuenta);
    };

    const procederConBorrado = (item) => {
        if (item.reflejadoIngresos) {
            setReflejadoDialog({ isOpen: true, item });
        } else {
            ejecutarBorrado(item);
        }
    };

    const deleteMovimiento = (item) => {
        setConfirmDialog({
            subtitle: '',
            title: '¿Desea borrar el movimiento?',
            isOpen: true,
            onConfirm: () => {
                setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
                if (item.folio) {
                    setFolioDialog({ isOpen: true, item });
                } else {
                    procederConBorrado(item);
                }
            },
        });
    };

    const ambosSeleccionados = selectedBanco && selectedCuenta;

    const toggleSeleccionado = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleAprobar = async () => {
        const payload = Array.from(selectedIds).map((id) => ({
            id,
            status: 'APROBADO',
            password: approvePassword,
        }));
        let resp;
        try {
            resp = await http.post('/movimientos-bancarios/aprobarMovimiento', payload, { validateStatus: () => true });
        } catch {
            resp = null;
        }
        if (!resp || resp.status < 200 || resp.status >= 300) {
            const msg = typeof resp?.data === 'string' ? resp.data : resp?.data?.message;
            dispatch(setSnackbar(true, 'error', msg || 'Error al aprobar el movimiento'));
            return;
        }
        setApproveDialogOpen(false);
        setApprovePassword('');
        setSelectedIds(new Set());
        cargarMovimientos(selectedBanco, selectedCuenta);
        dispatch(setSnackbar(true, 'success', 'El registro fue aprobado'));
    };


    const exportToExcel = () => {
        const datos = filterFn.fn(movimientosFiltradosPorFecha);
        const filas = datos.map((item) => ({
            ID: item.id ?? '',
            Fecha: item.fecha ? String(item.fecha).match(/(\d{4}-\d{2}-\d{2})/)?.[1] ?? item.fecha : '',
            Referencia: item.referencia ?? '',
            Clasificación: item.tipo ?? '',
            Factura: item.factura ?? '',
            Folio: item.folio ?? '',
            Descripción: item.descripcion ?? '',
            Depósito: item.deposito ?? '',
            Retiro: item.retiro ?? '',
            'Saldo Movimiento': item.saldoActual ?? '',
            Status: item.status ?? '',
        }));
        const worksheet = XLSX.utils.json_to_sheet(filas);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Movimientos');
        XLSX.writeFile(workbook, 'movimientos_bancarios.xlsx');
    };

    return (
        <>
            <PageHeader
                icon={<AccountBalanceIcon fontSize='large' />}
                subtitle=''
                title='Movimientos Bancarios'
            />

            {/* Filter section — always visible */}
            <Paper className={classes.filterSection}>
                <Typography className={classes.filterLabel} variant='subtitle1'>
                    Selecciona Banco y Cuenta
                </Typography>                
                <Grid container alignItems='center' spacing={3}>
                    <Grid item>
                        <Controls.Select
                            label='Banco'
                            name='banco'
                            onChange={handleBancoChange}
                            options={bancosOptions}
                            style={{ minWidth: 280 }}
                            value={selectedBanco}
                        />
                    </Grid>
                    <Grid item>
                        <Controls.Select
                            disabled={!selectedBanco}
                            label='Cuenta'
                            name='cuenta'
                            onChange={handleCuentaChange}
                            options={cuentasOptions}
                            style={{ minWidth: 320 }}
                            value={selectedCuenta}
                        />
                    </Grid>
                    {selectedCuenta && (
                        <Grid item sx={{ ml: 'auto' }}>
                            {saldoLoading && (
                                <Skeleton animation='wave' height={48} sx={{ borderRadius: 1 }} width={280} />
                            )}
                            {!saldoLoading && saldoError && (
                                <Typography sx={{ color: 'error.main', fontWeight: 'bold', fontSize: '0.95rem' }}>
                                    Necesitas definir un saldo inicial para esta cuenta para poder agregar registros
                                </Typography>
                            )}
                            {!saldoLoading && !saldoError && saldoCuenta !== null && (
                                <>
                                    <Typography
                                        component='span'
                                        sx={{ color: '#333996', fontWeight: 'bold', fontSize: '1.1rem', mr: 1 }}
                                    >
                                        Saldo de la cuenta:
                                    </Typography>
                                    <Typography
                                        component='span'
                                        sx={{ color: '#1a1a6e', fontWeight: 'bold', fontSize: '1.6rem' }}
                                    >
                                        {formatMoneda(saldoCuenta)}
                                    </Typography>
                                </>
                            )}
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* Table — only shown when both banco and cuenta are selected */}
            {ambosSeleccionados && (
                <Paper className={classes.pageContent}>
                    {movimientosLoading && (
                        <Box sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            justifyContent: 'center',
                            minHeight: 300,
                            width: '100%',
                        }}
                        >
                            <CircularProgress size={64} sx={{ color: '#333996' }} thickness={4} />
                            <Typography sx={{ color: '#333996', fontWeight: 'bold' }}>
                                Cargando movimientos...
                            </Typography>
                        </Box>
                    )}
                    {!movimientosLoading && (<>
                    <Toolbar>
                        <Controls.Button
                            className={classes.refreshButton}
                            onClick={() => cargarMovimientos(selectedBanco, selectedCuenta)}
                            startIcon={<RefreshIcon />}
                            text='Refrescar'
                            variant='outlined'
                        />
                        <Controls.Button
                            className={classes.newButton}
                            disabled={nuevoSaldoActual === null}
                            onClick={() => {
                                setRecordForEdit(null);
                                setOpenPopup(true);
                            }}
                            startIcon={<AddIcon />}
                            text='Agregar Nuevo'
                            variant='outlined'
                        />
                        <Controls.Button
                            disabled={selectedIds.size === 0}
                            onClick={() => setApproveDialogOpen(true)}
                            startIcon={<CheckCircleOutlineIcon />}
                            sx={{
                                backgroundColor: selectedIds.size === 0 ? undefined : '#1976d2',
                                color: selectedIds.size === 0 ? undefined : 'white',
                                '&:hover': { backgroundColor: '#1565c0' },
                            }}
                            text='Aprobar movimiento'
                        />
                        <Box sx={{ ml: 6, display: 'flex', gap: 2 }}>
                            <FiltroNumerico
                                key={`id-${resetFiltroId}`}
                                label='Filtrar por ID'
                                onApply={(valor) => {
                                    setResetFiltroFolio((k) => k + 1);
                                    limpiarBusquedaYFechas();
                                    setFilterFn(valor === ''
                                        ? { fn: (items) => items }
                                        : { fn: (items) => items.filter((item) => String(item.id) === valor) });
                                }}
                            />
                            <FiltroNumerico
                                key={`folio-${resetFiltroFolio}`}
                                label='Filtrar por Folio'
                                onApply={(valor) => {
                                    setResetFiltroId((k) => k + 1);
                                    limpiarBusquedaYFechas();
                                    setFilterFn(valor === ''
                                        ? { fn: (items) => items }
                                        : { fn: (items) => items.filter((item) => String(item.folio) === valor) });
                                }}
                            />
                            <Controls.Input
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                                label='Búsqueda General'
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFilterSearch(val);
                                    setSoloPendientes(false);
                                    setResetFiltroId((k) => k + 1);
                                    setResetFiltroFolio((k) => k + 1);
                                    setFilterFechaInicio('');
                                    setFilterFechaFin('');
                                    setAppliedFechaInicio('');
                                    setAppliedFechaFin('');
                                    setDateFilterApplied(false);
                                    setFilterFn({
                                        fn: (items) => {
                                            if (val === '') return items;
                                            return filterOnAnyField(items, val.toLowerCase());
                                        },
                                    });
                                }}
                                sx={{
                                    width: 320,
                                    '& .MuiOutlinedInput-root': { height: 36.5 },
                                    '& .MuiInputLabel-root': { top: -8 },
                                    '& .MuiInputLabel-shrink': { top: 0 },
                                }}
                                value={filterSearch}
                            />
                        </Box>
                    </Toolbar>

                    {/* Fila de filtro por rango de fechas */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, pb: 1 }}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            label='Fecha Inicio'
                            onChange={(e) => {
                                setFilterFechaInicio(e.target.value);
                                if (!e.target.value) {
                                    setFilterFechaFin('');
                                    setAppliedFechaInicio('');
                                    setAppliedFechaFin('');
                                    setDateFilterApplied(false);
                                }
                            }}
                            size='small'
                            sx={{ width: 180 }}
                            type='date'
                            value={filterFechaInicio}
                        />
                        <TextField
                            disabled={!filterFechaInicio}
                            InputLabelProps={{ shrink: true }}
                            label='Fecha Fin'
                            onChange={(e) => setFilterFechaFin(e.target.value)}
                            size='small'
                            sx={{ width: 180 }}
                            type='date'
                            value={filterFechaFin}
                        />
                        <Controls.Button
                            disabled={!filterFechaInicio}
                            onClick={() => {
                                // Limpia los otros filtros
                                setSoloPendientes(false);
                                setResetFiltroId((k) => k + 1);
                                setResetFiltroFolio((k) => k + 1);
                                setFilterSearch('');
                                setFilterFn({ fn: (items) => items });
                                // Congela los valores actuales para que useMemo filtre con ellos
                                setAppliedFechaInicio(filterFechaInicio);
                                setAppliedFechaFin(filterFechaFin);
                                setDateFilterApplied(true);
                            }}
                            sx={{
                                height: 36.5,
                                whiteSpace: 'nowrap',
                                backgroundColor: '#1976d2',
                                color: 'white',
                                '&:hover': { backgroundColor: '#1565c0' },
                                '&:disabled': { backgroundColor: undefined, color: undefined },
                            }}
                            text='Buscar por fecha'
                        />
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={soloPendientes}
                                    onChange={(e) => {
                                        const marcado = e.target.checked;
                                        if (marcado) {
                                            setResetFiltroId((k) => k + 1);
                                            setResetFiltroFolio((k) => k + 1);
                                            limpiarBusquedaYFechas();
                                        }
                                        setSoloPendientes(marcado);
                                        setFilterFn(marcado
                                            ? { fn: (items) => items.filter((item) => item.status !== 'APROBADO') }
                                            : { fn: (items) => items });
                                    }}
                                    size='small'
                                />
                            )}
                            label='Pendientes por Aprobar'
                            sx={{ ml: 4, whiteSpace: 'nowrap' }}
                        />
                        <Controls.Button
                            onClick={exportToExcel}
                            sx={{
                                ml: 'auto',
                                height: 36.5,
                                whiteSpace: 'nowrap',
                                backgroundColor: '#2e7d32',
                                color: 'white',
                                '&:hover': { backgroundColor: '#1b5e20' },
                            }}
                            text='Exportar a Excel'
                        />
                    </Box>

                    <TblPagination />
                    <Box sx={{
                        '& table': { borderCollapse: 'collapse' },
                        '& th': { border: '1px solid #9e9e9e', padding: '6px 10px', fontSize: '0.82rem', backgroundColor: '#e8eaf6' },
                        '& td': { border: '1px solid #c8c8c8', padding: '3px 10px', fontSize: '0.82rem' },
                    }}>
                    <TblContainer>
                        <TblHead />
                        <TableBody>
                            {recordsAfterPagingAndSorting().map((item) => (
                                <TableRow
                                    key={item.id}
                                    sx={{ backgroundColor: item.status === 'APROBADO' ? 'white' : '#fff9c4' }}
                                >
                                    <TableCell padding='checkbox'>
                                        <Checkbox
                                            checked={selectedIds.has(item.id)}
                                            disabled={item.status === 'APROBADO'}
                                            onChange={() => toggleSeleccionado(item.id)}
                                        />
                                    </TableCell>
                                    <TableCell>{item.id}</TableCell>
                                    <TableCell>{formatFecha(item.fecha)}</TableCell>
                                    <TableCell>{item.referencia}</TableCell>
                                    <TableCell>{item.tipo}</TableCell>
                                    <TableCell sx={{ width: 350 }}>
                                        {/* Máximo 350px; en pantallas chicas se angosta hasta 150px para que quepan las demás columnas */}
                                        <Box sx={{ maxWidth: 350, minWidth: 150, overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
                                            {item.factura}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {item.folio ? (
                                            <span
                                                onClick={() => history.push(`/viaje/${item.folio}`)}
                                                style={{ color: '#1565c0', cursor: 'pointer', textDecoration: 'underline' }}
                                            >
                                                {item.folio}
                                            </span>
                                        ) : ''}
                                    </TableCell>
                                    <TableCell>{item.descripcion}</TableCell>
                                    <TableCell
                                        align='right'
                                        sx={{ color: '#2e7d32', fontWeight: 500 }}
                                    >
                                        {item.deposito ? `+${formatMoneda(item.deposito)}` : ''}
                                    </TableCell>
                                    <TableCell
                                        align='right'
                                        sx={{ color: '#c62828', fontWeight: 500 }}
                                    >
                                        {item.retiro ? `-${formatMoneda(item.retiro)}` : ''}
                                    </TableCell>
                                    <TableCell align='right'>{formatMoneda(item.saldoActual)}</TableCell>
                                    <TableCell>
                                        <Controls.ActionButton
                                            color='primary'
                                            onClick={() => openInPopup(item)}
                                        >
                                            <EditOutlinedIcon fontSize='small' />
                                        </Controls.ActionButton>
                                        <Controls.ActionButton
                                            color='primary'
                                            onClick={() => deleteMovimiento(item)}
                                        >
                                            <DeleteForeverOutlined fontSize='small' />
                                        </Controls.ActionButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </TblContainer>
                    </Box>
                    <TblPagination />
                    </>)}
                </Paper>
            )}

            <Popup
                openPopup={openPopup}
                setOpenPopup={handleClosePopup}
                title='Movimiento Bancario'
            >
                <MovimientosBancariosForm
                    addOrEdit={addOrEdit}
                    checkUnsavedRef={checkUnsavedRef}
                    idCuenta={selectedCuenta}
                    recordForEdit={recordForEdit}
                    nuevoSaldoActual={nuevoSaldoActual}
                    saldoCuenta={saldoCuenta}
                />
            </Popup>

            {/* Diálogo: gastos sin guardar al cerrar modal de nuevo registro */}
            <Dialog onClose={() => setUnsavedGastosDialogOpen(false)} open={unsavedGastosDialogOpen}>
                <DialogTitle>¿Cerrar sin guardar?</DialogTitle>
                <DialogContent>
                    <Typography>
                        No has guardado el registro de movimientos bancarios, si continuas no se guardarán los
                        registros de
                        {' '}
                        <strong>OTROS GASTOS</strong>
                        {' '}
                        que hayas agregado/editado.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Controls.Button
                        color='default'
                        onClick={() => setUnsavedGastosDialogOpen(false)}
                        text='Continuar editando'
                        variant='outlined'
                    />
                    <Controls.Button
                        onClick={handleCloseWithoutSaving}
                        sx={{ backgroundColor: '#c62828', color: 'white', '&:hover': { backgroundColor: '#b71c1c' } }}
                        text='Cerrar la ventana sin guardar'
                    />
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                confirmDialog={confirmDialog}
                setConfirmDialog={setConfirmDialog}
            />

            {/* Diálogo: movimiento asociado a folio de viaje */}
            <Dialog open={folioDialog.isOpen}>
                <DialogTitle>Advertencia</DialogTitle>
                <DialogContent>
                    Cualquier registro de <strong>OTROS GASTOS</strong> asociado a este movimiento bancario será borrado, ¿desea continuar?
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 1 }}>
                    <Controls.Button
                        color='default'
                        onClick={() => setFolioDialog({ isOpen: false, item: null })}
                        text='Cancelar'
                        variant='outlined'
                    />
                    <Controls.Button
                        color='secondary'
                        onClick={() => {
                            const { item } = folioDialog;
                            setFolioDialog({ isOpen: false, item: null });
                            procederConBorrado(item);
                        }}
                        text='Sí, proceder con el borrado'
                    />
                </DialogActions>
            </Dialog>

            {/* Diálogo: registro reflejado en control ingresos */}
            <Dialog open={reflejadoDialog.isOpen}>
                <DialogTitle>Advertencia</DialogTitle>
                <DialogContent>
                    El registro tiene asociados otros registros en CONTROL INGRESOS, si procede con el borrado, se eliminarán esos registros también.
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 1 }}>
                    <Controls.Button
                        color='default'
                        onClick={() => setReflejadoDialog({ isOpen: false, item: null })}
                        text='Cancelar'
                        variant='outlined'
                    />
                    <Controls.Button
                        color='secondary'
                        onClick={async () => {
                            const { item } = reflejadoDialog;
                            setReflejadoDialog({ isOpen: false, item: null });
                            try {
                                await http.delete(
                                    `/control-ingresos/borrar-idMovBanc-idCuentaBanc?id_movimiento_bancario=${item.id}&id_cuenta_Bancaria=${item.idCuenta}`,
                                    { validateStatus: () => true },
                                );
                            } catch {
                                // si falla la limpieza de control ingresos, continuamos con el borrado
                            }
                            ejecutarBorrado(item);
                        }}
                        text='Proceder con el borrado'
                    />
                </DialogActions>
            </Dialog>

            {/* Diálogo de aprobación con password */}
            <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
                <DialogTitle>Aprobar movimientos</DialogTitle>
                <DialogContent>
                    <Typography variant='body2' sx={{ mb: 2 }}>
                        {`Se aprobarán ${selectedIds.size} movimiento(s). Ingresa el password para continuar.`}
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label='Password'
                        margin='dense'
                        onChange={(e) => setApprovePassword(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && approvePassword) handleAprobar(); }}
                        type='password'
                        value={approvePassword}
                    />
                </DialogContent>
                <DialogActions>
                    <Controls.Button
                        color='default'
                        onClick={() => { setApproveDialogOpen(false); setApprovePassword(''); }}
                        text='Cancelar'
                        variant='outlined'
                    />
                    <Controls.Button
                        disabled={!approvePassword}
                        onClick={handleAprobar}
                        startIcon={<CheckCircleOutlineIcon />}
                        text='Confirmar'
                    />
                </DialogActions>
            </Dialog>

            <Dialog onClose={() => setDeleteErrorDialogOpen(false)} open={deleteErrorDialogOpen}>
                <DialogTitle>No se puede borrar el registro</DialogTitle>
                <DialogContent>
                    <Typography>
                        Este registro está relacionado a otro registro en
                        {' '}
                        <strong>Control de Ingresos</strong>
                        , por lo que no se puede borrar directamente.
                    </Typography>
                    <Typography sx={{ mt: 1 }}>
                        Si deseas borrarlo, primero debes borrar el registro relacionado en
                        {' '}
                        <strong>Control Ingresos</strong>
                        .
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Controls.Button
                        onClick={() => setDeleteErrorDialogOpen(false)}
                        text='Entendido'
                        variant='contained'
                    />
                </DialogActions>
            </Dialog>
        </>
    );
}
